import { sendGAEvent } from '@noths/polaris-client-google-analytics';
import { EventsMap } from '@noths/polaris-client-next-redux';

import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectCurrentPage } from 'src/redux/selectors';
import { getImpressionFieldObjects } from 'src/utils/googleAnalytics';
import { requestProducts, updateFilters, UpdateFiltersActionType } from '../thunks';
import { ProductsState } from '../types';
import {
  FilterUpdateActionType,
  FilterUpdateImpressionsEvent,
  LoadImpressionsActionType,
  LoadProductImpressionsEvent,
} from './events';

type RequestProductsAction = ReturnType<typeof requestProducts['fulfilled']>;
type UpdateFiltersAction = ReturnType<typeof updateFilters['fulfilled']>;

const isLoadPreviousProductsRequest = (nextState: ReduxApplicationState, currentPage: number) => {
  return selectCurrentPage(nextState) > currentPage;
};

const isLoadMoreProductsRequest = (
  prevState: ReduxApplicationState,
  nextState: ReduxApplicationState,
  currentPage: number,
) => {
  return (
    selectCurrentPage(prevState) < selectCurrentPage(nextState) &&
    selectCurrentPage(nextState) === currentPage
  );
};

export const GAEventsMap: EventsMap<ReduxApplicationState> = {
  [requestProducts.fulfilled.type]: (prevState, nextState, action) => {
    const { payload } = action as RequestProductsAction;
    const isLoadPreviousRequest = isLoadPreviousProductsRequest(nextState, payload.currentPage);
    const isLoadMoreRequest = isLoadMoreProductsRequest(prevState, nextState, payload.currentPage);

    if (isLoadPreviousRequest || isLoadMoreRequest) {
      const eventAction: LoadImpressionsActionType = isLoadPreviousRequest
        ? 'Load Previous Impressions'
        : 'Load More Impressions';

      sendGAEvent<LoadProductImpressionsEvent>({
        event: 'product_impressions',
        event_action: eventAction,
        event_category: 'Product listings',
        event_label: String(selectCurrentPage(prevState)),
        ecommerce: {
          impressions: getImpressionFieldObjects({
            ...nextState,
            products: {
              ...((payload as Partial<ProductsState>) as ProductsState),
            },
          }),
        },
      });
    }
  },
  [updateFilters.fulfilled.type]: (state, _, action) => {
    const { payload } = action as UpdateFiltersAction;
    const event_action: FilterUpdateActionType =
      payload === UpdateFiltersActionType.FILTER ? 'Filtered Impressions' : 'Sorted Impressions';

    sendGAEvent<FilterUpdateImpressionsEvent>({
      event: 'product_impressions',
      event_action: event_action,
      event_category: 'Product listings',
      event_label: String(state.products.currentPage),
      ecommerce: {
        impressions: getImpressionFieldObjects(state),
      },
    });
  },
};
